import Phaser from "phaser";
import {
  DIRECTIONS,
  GAME_CONFIG,
  GHOST_HOUSE,
  MAP_LAYERS,
  POWER_PILL_POSITIONS,
  RENDER_DEPTHS,
  TIMING,
} from "../../constants/game";
import { Intersection, Point } from "../../types/game";

/**
 * BoardManager
 * Handles board setup, dots, power pills, intersections, and pathfinding
 */
export class BoardManager {
  private map: Phaser.Tilemaps.Tilemap;
  private blockSize: number;
  private board: number[][] = [];
  private dots: Phaser.Physics.Arcade.Group;
  private powerPills: Phaser.Physics.Arcade.Group;
  private intersections: Intersection[] = [];
  private dotsRegenerateTimer: ReturnType<typeof setInterval> | null = null;
  private getPillLocations?: () => Point[];

  constructor(
    _scene: Phaser.Scene,
    map: Phaser.Tilemaps.Tilemap,
    blockSize: number,
    dots: Phaser.Physics.Arcade.Group,
    powerPills: Phaser.Physics.Arcade.Group
  ) {
    this.map = map;
    this.blockSize = blockSize;
    this.dots = dots;
    this.powerPills = powerPills;
  }

  /**
   * Set callback to get current pill locations
   * Used to avoid placing dots on pills during regeneration
   */
  setPillLocationsCallback(callback: () => Point[]): void {
    this.getPillLocations = callback;
  }

  /**
   * Get the board grid
   */
  getBoard(): number[][] {
    return this.board;
  }

  /**
   * Get all intersections
   */
  getIntersections(): Intersection[] {
    return this.intersections;
  }

  /**
   * Populate board with dots and track empty tiles
   */
  populateBoardAndTrackEmptyTiles(layer: Phaser.Tilemaps.TilemapLayer): void {
    layer.forEachTile((tile) => {
      if (!this.board[tile.y]) {
        this.board[tile.y] = [];
      }
      this.board[tile.y][tile.x] = tile.index;
      if (
        tile.y < 1 ||
        (tile.y > 10 && tile.y < 20 && tile.x > 6 && tile.x < 21) ||
        (tile.y === 14 && tile.x !== 6 && tile.x !== 21)
      )
        return;
      const rightTile = this.map.getTileAt(
        tile.x + 1,
        tile.y,
        true,
        "Tile Layer 1"
      );
      const bottomTile = this.map.getTileAt(
        tile.x,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );
      const rightBottomTile = this.map.getTileAt(
        tile.x + 1,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );
      if (
        tile.index === -1 &&
        rightTile &&
        rightTile.index === -1 &&
        bottomTile &&
        bottomTile.index === -1 &&
        rightBottomTile &&
        rightBottomTile.index === -1
      ) {
        const x = tile.x * this.blockSize;
        const y = tile.y * this.blockSize;
        const dot = this.dots.create(
          x + this.blockSize,
          y + this.blockSize,
          "dot"
        );
        dot.setDepth(RENDER_DEPTHS.DOTS);
        // Scale dot to match new block size (approximately 1/3 of block size)
        dot.setDisplaySize(this.blockSize / 3, this.blockSize / 3);
      }
    });

    POWER_PILL_POSITIONS.forEach((position) => {
      const powerPill = this.powerPills.create(
        position.x,
        position.y,
        "powerPill"
      );
      powerPill.setDepth(RENDER_DEPTHS.POWER_PILLS);
      // Scale power pill to match new block size (approximately 2/3 of block size)
      powerPill.setDisplaySize(
        (this.blockSize * 2) / 3,
        (this.blockSize * 2) / 3
      );
    });
  }

  /**
   * Regenerate all dots and power pills on the game board
   * Clears existing dots and power pills, then recreates them using the same logic as initial population
   * This function is called every 30 seconds to refresh the collectible items
   */
  regenerateDotsAndPowerPills(): void {
    // Clear existing dots and power pills (with safety checks)
    // Avoid using clear() method which may cause errors - manually remove children instead
    if (this.dots && this.dots.children && this.dots.children.entries) {
      // Manually disable and remove all dots
      const dotsToRemove: Phaser.Physics.Arcade.Sprite[] = [];
      this.dots.children.entries.forEach((dot) => {
        const dotSprite = dot as Phaser.Physics.Arcade.Sprite & {
          active?: boolean;
          body?: Phaser.Physics.Arcade.Body;
          disableBody: (
            removeFromWorld: boolean,
            disableGameObject: boolean
          ) => void;
          destroy: () => void;
        };
        if (dotSprite && dotSprite.active) {
          dotsToRemove.push(dotSprite as Phaser.Physics.Arcade.Sprite);
        }
      });
      dotsToRemove.forEach((dot) => {
        const dotSprite = dot as Phaser.Physics.Arcade.Sprite & {
          body?: Phaser.Physics.Arcade.Body;
          disableBody: (
            removeFromWorld: boolean,
            disableGameObject: boolean
          ) => void;
          destroy: () => void;
        };
        if (dotSprite.body) {
          dotSprite.disableBody(true, true);
        } else {
          dotSprite.destroy();
        }
      });
    }

    if (
      this.powerPills &&
      this.powerPills.children &&
      this.powerPills.children.entries
    ) {
      // Manually disable and remove all power pills
      const pillsToRemove: Phaser.Physics.Arcade.Sprite[] = [];
      this.powerPills.children.entries.forEach((pill) => {
        const pillSprite = pill as Phaser.Physics.Arcade.Sprite & {
          active?: boolean;
          body?: Phaser.Physics.Arcade.Body;
          disableBody: (
            removeFromWorld: boolean,
            disableGameObject: boolean
          ) => void;
          destroy: () => void;
        };
        if (pillSprite && pillSprite.active) {
          pillsToRemove.push(pillSprite as Phaser.Physics.Arcade.Sprite);
        }
      });
      pillsToRemove.forEach((pill) => {
        const pillSprite = pill as Phaser.Physics.Arcade.Sprite & {
          body?: Phaser.Physics.Arcade.Body;
          disableBody: (
            removeFromWorld: boolean,
            disableGameObject: boolean
          ) => void;
          destroy: () => void;
        };
        if (pillSprite.body) {
          pillSprite.disableBody(true, true);
        } else {
          pillSprite.destroy();
        }
      });
    }

    // Get the tilemap layer to regenerate items
    const layer = this.map.getLayer(MAP_LAYERS.TILE_LAYER_1)?.tilemapLayer;
    if (!layer) return;

    // Get current pill locations to avoid placing dots there
    const pillLocations: Point[] = this.getPillLocations
      ? this.getPillLocations()
      : [];

    // Helper function to check if a location is occupied by a pill
    const isPillLocation = (x: number, y: number): boolean => {
      return pillLocations.some(
        (pillLoc) =>
          Math.abs(pillLoc.x - x) < this.blockSize * 2 &&
          Math.abs(pillLoc.y - y) < this.blockSize * 2
      );
    };

    // Regenerate dots using the same logic as populateBoardAndTrackEmptyTiles
    layer.forEachTile((tile) => {
      // Skip tiles that shouldn't have dots (same conditions as original function)
      if (
        tile.y < 1 ||
        (tile.y > 10 && tile.y < 20 && tile.x > 6 && tile.x < 21) ||
        (tile.y === 14 && tile.x !== 6 && tile.x !== 21)
      )
        return;

      const rightTile = this.map.getTileAt(
        tile.x + 1,
        tile.y,
        true,
        "Tile Layer 1"
      );
      const bottomTile = this.map.getTileAt(
        tile.x,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );
      const rightBottomTile = this.map.getTileAt(
        tile.x + 1,
        tile.y + 1,
        true,
        "Tile Layer 1"
      );

      // Create dot if all four tiles in 2x2 area are empty
      if (
        tile.index === -1 &&
        rightTile &&
        rightTile.index === -1 &&
        bottomTile &&
        bottomTile.index === -1 &&
        rightBottomTile &&
        rightBottomTile.index === -1
      ) {
        const x = tile.x * this.blockSize;
        const y = tile.y * this.blockSize;
        const dotX = x + this.blockSize;
        const dotY = y + this.blockSize;

        // Skip this location if a pill is currently placed here
        if (isPillLocation(dotX, dotY)) {
          return;
        }

        const dot = this.dots.create(dotX, dotY, "dot");
        dot.setDepth(RENDER_DEPTHS.DOTS);
        // Scale dot to match new block size (approximately 1/3 of block size)
        dot.setDisplaySize(this.blockSize / 3, this.blockSize / 3);
      }
    });

    // Regenerate power pills at fixed positions
    POWER_PILL_POSITIONS.forEach((position) => {
      const powerPill = this.powerPills.create(
        position.x,
        position.y,
        "powerPill"
      );
      powerPill.setDepth(RENDER_DEPTHS.POWER_PILLS);
      // Scale power pill to match new block size (approximately 2/3 of block size)
      powerPill.setDisplaySize(
        (this.blockSize * 2) / 3,
        (this.blockSize * 2) / 3
      );
    });
  }

  /**
   * Start the automatic regeneration timer
   * Sets up a repeating timer that regenerates dots and power pills every 30 seconds
   */
  startRegenerationTimer(): void {
    this.dotsRegenerateTimer = setInterval(() => {
      this.regenerateDotsAndPowerPills();
    }, TIMING.DOTS_REGENERATION_INTERVAL);
  }

  /**
   * Stop the automatic regeneration timer
   * Clears the timer to prevent further regeneration
   */
  stopRegenerationTimer(): void {
    if (this.dotsRegenerateTimer) {
      clearInterval(this.dotsRegenerateTimer);
      this.dotsRegenerateTimer = null;
    }
  }

  /**
   * Check if a point is clear of walls by examining all four corners
   * Used to determine if a position is passable for characters
   * @param x - X coordinate to check
   * @param y - Y coordinate to check
   * @returns True if the point is clear, false if blocked by walls
   */
  isPointClear(x: number, y: number): boolean {
    const corners = [
      { x: x - 1, y: y - 1 },
      { x: x + 1, y: y - 1 },
      { x: x - 1, y: y + 1 },
      { x: x + 1, y: y + 1 },
    ];
    return corners.every((corner) => {
      const tileX = Math.floor(corner.x / this.blockSize);
      const tileY = Math.floor(corner.y / this.blockSize);

      return !this.board[tileY] || this.board[tileY][tileX] === -1;
    });
  }

  /**
   * Detect all intersections in the maze for pathfinding
   * Scans the entire map to find points where characters can change direction
   * Identifies T-junctions (3+ paths) and corners (2 perpendicular paths)
   * Excludes areas outside the playable maze boundaries
   */
  detectIntersections(): void {
    const directions = [
      { x: -this.blockSize, y: 0, name: DIRECTIONS.LEFT },
      { x: this.blockSize, y: 0, name: DIRECTIONS.RIGHT },
      { x: 0, y: -this.blockSize, name: DIRECTIONS.UP },
      { x: 0, y: this.blockSize, name: DIRECTIONS.DOWN },
    ];
    const blockSize = this.blockSize;
    // Use scaled dimensions from GAME_CONFIG instead of map dimensions
    const scaledHeight = GAME_CONFIG.HEIGHT;
    const scaledWidth = GAME_CONFIG.WIDTH;
    for (let y = 0; y < scaledHeight; y += blockSize) {
      for (let x = 0; x < scaledWidth; x += blockSize) {
        if (x % blockSize !== 0 || y % blockSize !== 0) continue;
        if (!this.isPointClear(x, y)) continue;
        const openPaths: string[] = [];
        directions.forEach((dir) => {
          if (this.isPathOpenAroundPoint(x + dir.x, y + dir.y)) {
            openPaths.push(dir.name);
          }
        });
        if (openPaths.length > 2 && y > 48 && y < 1446) {
          this.intersections.push({ x: x, y: y, openPaths: openPaths });
        } else if (openPaths.length === 2 && y > 48 && y < 1446) {
          const [dir1, dir2] = openPaths;
          if (
            ((dir1 === DIRECTIONS.LEFT || dir1 === DIRECTIONS.RIGHT) &&
              (dir2 === DIRECTIONS.UP || dir2 === DIRECTIONS.DOWN)) ||
            ((dir1 === DIRECTIONS.UP || dir1 === DIRECTIONS.DOWN) &&
              (dir2 === DIRECTIONS.LEFT || dir2 === DIRECTIONS.RIGHT))
          ) {
            this.intersections.push({ x: x, y: y, openPaths: openPaths });
          }
        }
      }
    }
  }

  /**
   * Check if a path is open around a specific point
   * Verifies that all four corners of a block are clear (not walls)
   * Used to determine if a direction is passable from an intersection
   * @param pixelX - X coordinate in pixels
   * @param pixelY - Y coordinate in pixels
   * @returns True if the path is open, false if blocked by walls
   */
  isPathOpenAroundPoint(pixelX: number, pixelY: number): boolean {
    const corners = [
      { x: pixelX - 1, y: pixelY - 1 },
      { x: pixelX + 1, y: pixelY - 1 },
      { x: pixelX - 1, y: pixelY + 1 },
      { x: pixelX + 1, y: pixelY + 1 },
    ];
    return corners.every((corner) => {
      const tileX = Math.floor(corner.x / this.blockSize);
      const tileY = Math.floor(corner.y / this.blockSize);
      if (!this.board[tileY] || this.board[tileY][tileX] !== -1) {
        return false;
      }
      return true;
    });
  }

  /**
   * A* pathfinding algorithm to find the optimal path between two points
   * Uses Manhattan distance heuristic and avoids the ghost house
   * @param start - Starting point for pathfinding
   * @param target - Target point to reach
   * @returns Array of points representing the optimal path, or empty array if no path found
   */
  aStarAlgorithm(start: Point, target: Point): Point[] {
    const isInGhostHouse = this.isInGhostHouse.bind(this);
    const blockSize = this.blockSize;

    function findNearestIntersection(
      point: Point,
      intersections: Intersection[]
    ): Intersection | null {
      let nearest = null;
      let minDist = Infinity;
      for (const intersection of intersections) {
        // Skip intersections inside ghost house, but allow entrance area
        // Entrance is at y=624, ghost house top is at y=672, so entrance is above ghost house
        if (isInGhostHouse(intersection.x, intersection.y)) {
          continue;
        }
        const dist =
          Math.abs(intersection.x - point.x) +
          Math.abs(intersection.y - point.y);
        if (dist < minDist) {
          minDist = dist;
          nearest = intersection;
        }
      }
      // If no intersection found and point is near ghost house entrance,
      // try to find any intersection within reasonable distance
      if (
        !nearest &&
        Math.abs(point.y - GHOST_HOUSE.ENTRANCE_Y) < blockSize * 5
      ) {
        for (const intersection of intersections) {
          if (isInGhostHouse(intersection.x, intersection.y)) {
            continue;
          }
          const dist =
            Math.abs(intersection.x - point.x) +
            Math.abs(intersection.y - point.y);
          if (dist < blockSize * 10) {
            // Within 10 blocks
            if (!nearest || dist < minDist) {
              minDist = dist;
              nearest = intersection;
            }
          }
        }
      }
      return nearest;
    }

    const startIntersection = findNearestIntersection(
      start,
      this.intersections
    );

    const targetIntersection = findNearestIntersection(
      target,
      this.intersections
    );

    if (!startIntersection || !targetIntersection) {
      return [];
    }

    const openList = [];
    const closedList = new Set();
    const cameFrom = new Map();
    const gScore = new Map();

    openList.push({
      node: startIntersection,
      g: 0,
      f: heuristic(startIntersection, targetIntersection),
    });
    gScore.set(JSON.stringify(startIntersection), 0);

    function heuristic(node: Point, target: Point) {
      return Math.abs(node.x - target.x) + Math.abs(node.y - target.y);
    }

    while (openList.length > 0) {
      openList.sort((a, b) => a.f - b.f);
      const current: Point = openList.shift()?.node ?? { x: 0, y: 0 };

      if (
        current.x === targetIntersection.x &&
        current.y === targetIntersection.y
      ) {
        const path: Point[] = [];
        let currentNode = current;
        while (cameFrom.has(JSON.stringify(currentNode))) {
          path.push(currentNode);
          currentNode = cameFrom.get(JSON.stringify(currentNode));
        }
        path.push(startIntersection);
        return path.reverse();
      }

      closedList.add(JSON.stringify(current));

      const currentIntersection = this.intersections.find(
        (i) => i.x === current.x && i.y === current.y
      );

      if (currentIntersection) {
        for (const direction of currentIntersection.openPaths) {
          const neighbor = this.getNextIntersection(
            current.x,
            current.y,
            direction
          );

          if (
            neighbor &&
            !isInGhostHouse(neighbor.x, neighbor.y) &&
            !closedList.has(JSON.stringify(neighbor))
          ) {
            const tentativeGScore = gScore.get(JSON.stringify(current)) + 1;

            if (
              !gScore.has(JSON.stringify(neighbor)) ||
              tentativeGScore < (gScore.get(JSON.stringify(neighbor)) || 0)
            ) {
              gScore.set(JSON.stringify(neighbor), tentativeGScore);
              const fScore =
                tentativeGScore + heuristic(neighbor, targetIntersection);
              openList.push({ node: neighbor, g: tentativeGScore, f: fScore });
              cameFrom.set(JSON.stringify(neighbor), current);
            }
          }
        }
      }
    }

    return [];
  }

  /**
   * Get the next intersection in a given direction
   * @param x - Current X coordinate
   * @param y - Current Y coordinate
   * @param direction - Direction to look in
   * @returns Next intersection point or null
   */
  getNextIntersection(x: number, y: number, direction: string): Point | null {
    let dx = 0,
      dy = 0;

    switch (direction) {
      case DIRECTIONS.LEFT:
        dx = -this.blockSize;
        break;
      case DIRECTIONS.RIGHT:
        dx = this.blockSize;
        break;
      case DIRECTIONS.UP:
        dy = -this.blockSize;
        break;
      case DIRECTIONS.DOWN:
        dy = this.blockSize;
        break;
    }

    let newX = x + dx;
    let newY = y + dy;

    // Find next intersection along this direction
    while (this.isPointClear(newX, newY)) {
      const intersection = this.intersections.find(
        (i) => i.x === newX && i.y === newY
      );
      if (intersection) {
        return intersection;
      }
      newX += dx;
      newY += dy;

      // Safety check to prevent infinite loops (use scaled dimensions)
      if (
        newX < 0 ||
        newX >= GAME_CONFIG.WIDTH ||
        newY < 0 ||
        newY >= GAME_CONFIG.HEIGHT
      ) {
        break;
      }
    }

    return null;
  }

  /**
   * Check if a point is inside the ghost house area
   * Ghost house is the central area where ghosts start and respawn
   * @param x - X coordinate to check
   * @param y - Y coordinate to check
   * @returns True if the point is inside the ghost house, false otherwise
   */
  isInGhostHouse(x: number, y: number): boolean {
    return (
      x <= GHOST_HOUSE.RIGHT &&
      x >= GHOST_HOUSE.LEFT &&
      y <= GHOST_HOUSE.BOTTOM &&
      y > GHOST_HOUSE.TOP
    );
  }
}
